<template>
  <section id="view-login">
    <div class="container-fields">
      <span v-if="env" class="container-environment-id">{{ env }}</span>
      <div class="container-logo">
        <img src="~@/assets/img/logo_ordexa.svg" alt="" />
      </div>
      <template v-if="step === 'code'">
        <div class="container-text-do-login">Confirme que é você</div>
        <p class="code-explain">
          Esta entrada é diferente do seu padrão. Enviamos um código de 6
          dígitos para <strong>{{ challenge.emailHint }}</strong>. Ele vale por
          15 minutos.
        </p>
        <div class="container-group-input">
          <label for="login-code">Código de verificação</label>
          <input
            id="login-code"
            ref="codeInput"
            v-model="code"
            class="code-input"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="6"
            placeholder="000000"
            :disabled="requestingCode"
            @input="onCodeInput"
            @paste.prevent="onCodePaste"
            @keyup.enter="verifyCode"
          />
          <div v-if="codeError" class="code-error" role="alert">
            {{ codeError }}
          </div>
        </div>
        <div class="container-remember-me">
          <label class="remember-checkbox">
            <input v-model="trustDevice" type="checkbox" />
            <span>Confiar neste dispositivo por 90 dias</span>
          </label>
        </div>
        <div class="container-button">
          <button
            id="verify-button"
            type="button"
            class="btn-default btn-primary"
            :disabled="requestingCode || code.length !== 6"
            @click="verifyCode"
          >
            <template v-if="requestingCode">
              <img
                class="icon-circle-segment-animated"
                src="~@/assets/img/ico/ico_circle_segment_white.svg"
                alt=""
              />
              <span>Aguarde</span>
            </template>
            <span v-else>Confirmar</span>
          </button>
        </div>
        <div class="container-help-link">
          <button
            type="button"
            class="link-button"
            :disabled="resendIn > 0 || resending"
            @click="resendCode"
          >
            {{ resendIn > 0 ? `Reenviar código (${resendIn}s)` : "Reenviar código" }}
          </button>
          <span class="separator">·</span>
          <button type="button" class="link-button" @click="backToPassword">
            Voltar
          </button>
        </div>
        <div class="container-help-link support-line">
          Não recebeu? Confira o spam ou fale com
          <a href="mailto:suporte@ordexa.com.br">suporte@ordexa.com.br</a>
        </div>
      </template>
      <template v-else-if="step === 'link-sent'">
        <div class="container-text-do-login">Confira seu e-mail</div>
        <p class="code-explain">
          Se existir uma conta do Ordexa com <strong>{{ linkEmail }}</strong>,
          acabamos de enviar para ele um link para entrar sem senha. O link só
          funciona uma vez.
        </p>
        <p class="code-explain">
          Não chegou em alguns minutos? Confira o spam e a aba Promoções, ou
          veja se o e-mail está certo.
        </p>
        <div class="container-button">
          <button type="button" class="btn-default btn-secondary" @click="step = 'password'">
            Voltar ao login
          </button>
        </div>
        <div class="container-help-link support-line">
          Ainda sem acesso? Fale com
          <a href="mailto:suporte@ordexa.com.br">suporte@ordexa.com.br</a>
        </div>
      </template>
      <template v-else>
      <div class="container-text-do-login">Faça seu login</div>
      <ComponentGoogleButton where="login" @success="googleSuccess" />
      <div class="container-group-input">
        <label>E-mail</label>
        <ComponentInputText
          id="email"
          v-model="model.user"
          :icon="require('@/assets/img/ico/ico_envelope.svg')"
          autocomplete="username"
          placeholder="seu e-mail"
          type="email"
          show-password-button="false"
          @keyup-enter="inputKeyupEnter"
        ></ComponentInputText>
        <button
          v-if="emailSuggestion"
          type="button"
          class="email-suggestion"
          @click="model.user = emailSuggestion"
        >
          Você quis dizer <b>{{ emailSuggestion }}</b>?
        </button>
      </div>
      <div class="container-group-input">
        <label>Senha</label>
        <ComponentInputText
          id="password"
          v-model="model.password"
          :icon="require('@/assets/img/ico/ico_key.svg')"
          placeholder="sua senha"
          autocomplete="current-password"
          type="password"
          show-password-button="true"
          @keyup-enter="inputKeyupEnter"
        ></ComponentInputText>
        <div class="container-link-forgot-password">
          <router-link :to="{ name: 'ForgotPassword' }"
            >Esqueci a senha</router-link
          >
        </div>
      </div>
      <div class="container-remember-me">
        <label class="remember-checkbox">
          <input v-model="rememberMe" type="checkbox" />
          <span>Lembrar de mim</span>
        </label>
      </div>
      <ComponentTurnstile ref="turnstile" @token="turnstileToken = $event" />
      <div class="container-button">
        <button
          id="login-button"
          type="button"
          class="btn-default btn-primary"
          :disabled="requestingLogin"
          @click="btnSignInClick"
        >
          <template v-if="requestingLogin">
            <img
              class="icon-circle-segment-animated"
              src="~@/assets/img/ico/ico_circle_segment_white.svg"
              alt=""
            />
            <span>Aguarde</span>
          </template>
          <span v-else>Entrar</span>
        </button>
      </div>
      <div :class="['login-link-box', { highlight: failedLogin || offerLink }]">
        <span v-if="failedLogin">Não lembra a senha?</span>
        <button
          type="button"
          class="link-button"
          :disabled="sendingLink"
          @click="sendLoginLink"
        >
          {{ sendingLink ? "Enviando..." : "Entrar sem senha: receber um link no e-mail" }}
        </button>
      </div>
      <div class="container-help-link">
        Ainda não tem conta?
        <router-link :to="{ path: '/signup', query: { plan: 'free' } }">Crie grátis</router-link>
      </div>
      <div class="container-help-link">
        Problemas com o acesso? <br v-if="$mq.current === 'sm'" /><a
          target="_blank"
          href="https://ordexa.com.br/guia-pratico"
          >Ajuda</a
        >
      </div>
      </template>
    </div>
  </section>
</template>

<script>
import ComponentTurnstile from "@/components/ui/ComponentTurnstile";
import MixinAuth from "@/mixins/MixinAuth.js";
import MixinView from "@/mixins/MixinView.js";

import ComponentInputText from "@/components/ui/ComponentInputText";
import ComponentGoogleButton from "@/components/ComponentGoogleButton";
import { suggestEmail } from "@/utils/emailTypo";
import { track } from "@/utils/analytics";

export default {
  name: "Login",
  components: {
    ComponentGoogleButton,
    ComponentInputText,
    ComponentTurnstile,
  },
  mixins: [MixinView, MixinAuth], //unmounted
  beforeRouteEnter(to, from, next) {
    next((vm) => {});
  }, //beforeRouteEnter
  beforeRouteUpdate(to, from, next) {
    next();
  }, //beforeRouteUpdate
  beforeRouteLeave(to, from, next) {
    next();
  },
  props: [], //components
  data() {
    return {
      turnstileToken: "",
      failedLogin: false,
      offerLink: !!this.$route.query.link,
      sendingLink: false,
      linkEmail: "",
      model: {
        user: typeof this.$route.query.email === "string" ? this.$route.query.email : "",
        password: "",
      },

      rememberMe: true,
      requestingLogin: false,

      step: "password",
      challenge: { id: "", emailHint: "" },
      code: "",
      codeError: "",
      trustDevice: true,
      requestingCode: false,
      resending: false,
      resendIn: 0,
      resendTimer: null,
    };
  }, //data
  computed: {
    emailSuggestion() {
      return suggestEmail(this.model.user);
    },
    env() {
      const vueAppEnv = process.env.VUE_APP_ENV;

      let ret = false;

      if (vueAppEnv) {
        if (vueAppEnv !== "production") {
          ret = vueAppEnv;
        }
      } else {
        ret = "not set";
      }

      return ret;
    },
  }, //methods
  watch: {}, //watch
  created() {}, //created
  beforeMount() {}, //beforeMount
  mounted() {
    this.checkParamAction();

    this.checkQueryAction();
  }, //mounted
  updated() {}, //updated
  unmounted() {}, //computed
  beforeUnmount() {
    clearInterval(this.resendTimer);
  },
  methods: {
    checkParamAction() {
      const action = this.$route.params.action;

      const messages = {
        "SESSION_EXPIRED:DO_LOGIN_AGAIN":
          "Sua sessão expirou. Por favor, faça o login novamente.",
      };

      if (messages[action]) {
        this.$eventbus.emit("ComponentToast/add", {
          autoHide: true,
          autoHideDelay: 10000,
          content: messages[action],
          icon: null,
          showCloseButton: false,
          type: "error",
        });
      }
    },

    checkQueryAction() {
      const action = this.$route.query.action;

      if (action === "new_password_requested") {
        this.$eventbus.emit("ComponentToast/add", {
          autoHide: true,
          autoHideDelay: 10000,
          content:
            "As instruções para criação de uma nova senha foram enviadas ao seu email cadastrado. Confira o link no seu email.",
          icon: null,
          showCloseButton: false,
          type: "success",
        });
      }

      if (action === "changed_password") {
        this.$eventbus.emit("ComponentToast/add", {
          autoHide: true,
          autoHideDelay: 10000,
          content:
            "Nova senha criada com sucesso. Utilize sua nova senha para acessar o Ordexa.",
          icon: null,
          showCloseButton: false,
          type: "success",
        });
      }
    },

    async signIn() {
      this.requestingLogin = true;

      const payload = {
        "user[email]": this.model.user,
        "user[password]": this.model.password,
      };
      const trustedToken = this.getTrustedToken();
      if (trustedToken) payload.trusted_device_token = trustedToken;
      const turnstile = await this.$refs.turnstile?.token();
      if (turnstile) payload.turnstile_token = turnstile;

      this.axios
        .post("/api/v1/auth/sign_in", payload)
        .then((response) => {
          if (response.status === 202 && response.data.verification_required) {
            this.showCodeStep(response.data);
            return;
          }
          this.setAuth(response, { remember: this.rememberMe });
        })
        .catch((error) => {
          if (error.response) {
            const data = error.response.data;
            if (error.response.status === 401) this.failedLogin = true;

            if (data.errors) {
              data.errors.forEach((item, index, array) => {
                this.$eventbus.emit("ComponentToast/add", {
                  autoHide: true,
                  content: item,
                  icon: null,
                  showCloseButton: false,
                  type: "error",
                });
              });
            } else {
              this.$eventbus.emit("ComponentToast/add", {
                autoHide: true,
                content: data.error || "Erro ao autenticar. Tente novamente.",
                icon: null,
                showCloseButton: false,
                type: "error",
              });
            }
          }

          this.requestingLogin = false;
          this.$refs.turnstile?.reset();
        });
    },

    // "Entrar sem senha": the API answers the same for any e-mail, so the screen does too.
    async sendLoginLink() {
      const email = String(this.model.user || "").trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        this.$eventbus.emit("ComponentToast/add", {
          autoHide: true,
          content: "Digite seu e-mail no campo acima para receber o link.",
          icon: null,
          showCloseButton: false,
          type: "error",
        });
        const el = document.getElementById("email");
        (el && el.tagName === "INPUT" ? el : el?.querySelector("input"))?.focus();
        return;
      }
      this.sendingLink = true;
      try {
        const payload = { email };
        const turnstile = await this.$refs.turnstile?.token();
        if (turnstile) payload.turnstile_token = turnstile;
        await this.axios.post("/api/v1/auth/login_link", payload, { skipGlobalErrorHandler: true });
        track("login_link_requested", { after_failure: this.failedLogin });
        this.linkEmail = email;
        this.step = "link-sent";
      } catch (e) {
        const data = e.response && e.response.data;
        const tooMany = e.response && e.response.status === 429;
        this.$eventbus.emit("ComponentToast/add", {
          autoHide: true,
          content: tooMany
            ? "Você já pediu alguns links agora. Confira seu e-mail ou tente de novo em uma hora."
            : (data && data.errors && data.errors[0]) || "Não foi possível enviar o link. Tente de novo.",
          icon: null,
          showCloseButton: false,
          type: "error",
        });
      } finally {
        this.sendingLink = false;
        this.$refs.turnstile?.reset();
      }
    },

    // Trusted-device token (kept per e-mail in this browser; the server only stores a digest).
    getTrustedToken() {
      try {
        const map = JSON.parse(localStorage.getItem("ordexa/trusted_device") || "{}");
        return map[String(this.model.user).trim().toLowerCase()] || null;
      } catch (e) {
        return null;
      }
    },

    saveTrustedToken(token) {
      try {
        const map = JSON.parse(localStorage.getItem("ordexa/trusted_device") || "{}");
        map[String(this.model.user).trim().toLowerCase()] = token;
        localStorage.setItem("ordexa/trusted_device", JSON.stringify(map));
      } catch (e) {
        // private window or blocked storage: the code is just asked again next time
      }
    },

    googleSuccess(response) {
      if (response.status === 202 && response.data.verification_required) {
        this.showCodeStep(response.data);
        return;
      }
      this.setAuth(response, { remember: true });
    },
    showCodeStep(data) {
      this.challenge = { id: data.challenge_id, emailHint: data.email_hint };
      this.code = "";
      this.codeError = "";
      this.trustDevice = true;
      this.step = "code";
      this.requestingLogin = false;
      this.startResendTimer(data.retry_in || 60);
      this.$nextTick(() => this.$refs.codeInput && this.$refs.codeInput.focus());
    },

    startResendTimer(seconds) {
      clearInterval(this.resendTimer);
      this.resendIn = seconds;
      this.resendTimer = setInterval(() => {
        this.resendIn = Math.max(this.resendIn - 1, 0);
        if (this.resendIn === 0) clearInterval(this.resendTimer);
      }, 1000);
    },

    backToPassword() {
      clearInterval(this.resendTimer);
      this.step = "password";
      this.code = "";
      this.codeError = "";
      this.model.password = "";
    },

    onCodeInput() {
      this.code = this.code.replace(/\D/g, "").slice(0, 6);
      this.codeError = "";
      if (this.code.length === 6) this.verifyCode();
    },

    onCodePaste(e) {
      const text = (e.clipboardData || window.clipboardData).getData("text");
      this.code = String(text).replace(/\D/g, "").slice(0, 6);
      this.codeError = "";
      if (this.code.length === 6) this.verifyCode();
    },

    verifyCode() {
      if (this.requestingCode || this.code.length !== 6) return;
      this.requestingCode = true;
      this.codeError = "";

      this.axios
        .post(
          "/api/v1/auth/verify_login",
          {
            challenge_id: this.challenge.id,
            code: this.code,
            trust_device: this.trustDevice,
          },
          { skipGlobalErrorHandler: true },
        )
        .then((response) => {
          if (response.data.trusted_device_token) {
            this.saveTrustedToken(response.data.trusted_device_token);
          }
          this.setAuth(response, { remember: this.rememberMe });
        })
        .catch((error) => {
          const data = (error.response && error.response.data) || {};
          this.codeError =
            (data.errors && data.errors[0]) ||
            "Não foi possível confirmar agora. Tente de novo.";
          this.code = "";
          if (data.expired) {
            // the request is gone: back to the password step with the message
            setTimeout(() => this.backToPassword(), 2500);
          } else {
            this.$nextTick(() => this.$refs.codeInput && this.$refs.codeInput.focus());
          }
        })
        .finally(() => {
          this.requestingCode = false;
        });
    },

    resendCode() {
      if (this.resendIn > 0 || this.resending) return;
      this.resending = true;
      this.axios
        .post(
          "/api/v1/auth/resend_login_code",
          { challenge_id: this.challenge.id },
          { skipGlobalErrorHandler: true },
        )
        .then(({ data }) => {
          this.codeError = "";
          this.startResendTimer(data.retry_in || 60);
          this.$eventbus.emit("ComponentToast/add", {
            autoHide: true,
            content: "Enviamos um novo código para o seu e-mail.",
            icon: null,
            showCloseButton: false,
            type: "success",
          });
        })
        .catch((error) => {
          const data = (error.response && error.response.data) || {};
          if (data.retry_in) this.startResendTimer(data.retry_in);
          this.codeError =
            (data.errors && data.errors[0]) ||
            "Não conseguimos reenviar agora. Tente de novo em instantes.";
          if (data.expired) setTimeout(() => this.backToPassword(), 2500);
        })
        .finally(() => {
          this.resending = false;
        });
    },

    //Events

    btnSignInClick(e) {
      this.signIn();
    },

    inputKeyupEnter(e) {
      this.signIn();
    },
  }, //beforeRouteLeave
};
</script>

<style scoped>
#view-login {
  animation: fadeIn 0.4s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.container-fields {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 2.5em;
  transition: box-shadow 0.3s ease;
}

@media (max-width: 767px) {
  .container-fields {
    padding: 1.75em;
  }
}

.container-fields:hover {
  box-shadow: 0 6px 30px rgba(0, 0, 0, 0.12);
}

.container-text-do-login {
  font-size: 1.05em;
  font-weight: 600;
}

.container-group-input {
  font-size: 0.9em;
  margin: 0 auto 0.8em;
  width: 100%;
}

.container-group-input .component-input-text {
  width: 100%;
}

.container-link-forgot-password {
  margin-top: 0.5em;
  text-align: right;
}

.container-link-forgot-password a {
  transition: color 0.2s ease;
}

.container-remember-me {
  margin: 1em 0;
}

.remember-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  font-size: 0.9em;
}

.remember-checkbox input[type="checkbox"] {
  width: 18px;
  height: 18px;
  margin-right: 0.5em;
  cursor: pointer;
  accent-color: #29b1cc;
}

.remember-checkbox span {
  color: #555;
  transition: color 0.2s ease;
}

.remember-checkbox:hover span {
  color: #29b1cc;
}

.container-button {
  margin-bottom: 2em;
}

.container-button {
  margin-bottom: 1.5em;
}

.container-button button {
  box-shadow: 0 4px 16px rgba(41, 177, 204, 0.3);
  font-size: 0.9em;
  padding-left: 3em;
  padding-right: 3em;
  transition: all 0.3s ease;
}

.container-button button:hover:not(:disabled) {
  box-shadow: 0 6px 20px rgba(41, 177, 204, 0.4);
  transform: translateY(-2px);
}

.container-button button:active:not(:disabled) {
  transform: translateY(0);
}

.container-button button img {
  margin-right: 0.5em;
}

.container-environment-id {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  background-color: #fd0;
  border-radius: 100px;
  cursor: pointer;
  font-size: 0.675em;
  font-weight: bold;
  letter-spacing: 0.015em;
  height: 2em;
  line-height: 100%;
  margin-bottom: 1em;
  margin-right: 1em;
  padding: 0 1em;
  text-align: center;
  text-transform: uppercase;
}

.container-help-link {
  font-size: 0.9em;
  line-height: 1.5;
  padding: 0.3em 0;
}

.code-explain {
  font-size: 0.9em;
  color: #555;
  margin: 0 0 1.2em;
  line-height: 1.5;
}

.code-input {
  width: 100%;
  box-sizing: border-box;
  margin-top: 0.4em;
  padding: 0.5em 0.4em;
  font-size: 1.75rem; /* 16px+ keeps iOS from zooming */
  font-weight: 600;
  letter-spacing: 0.4em;
  text-align: center;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
}

.code-input:focus {
  outline: none;
  border-color: #29b1cc;
  box-shadow: 0 0 0 3px rgba(41, 177, 204, 0.2);
}

.code-error {
  margin-top: 0.5em;
  color: #c62828;
  font-size: 0.95em;
}

.link-button {
  background: none;
  border: 0;
  padding: 0.5em 0.25em;
  color: #29b1cc;
  font: inherit;
  cursor: pointer;
  text-decoration: underline;
}

.link-button:disabled {
  color: #999;
  cursor: default;
  text-decoration: none;
}

.separator {
  color: #999;
  margin: 0 0.25em;
}

.support-line {
  margin-top: 0.6em;
  color: #666;
}

@media (max-width: 767px) {
}

@media (min-width: 768px) and (max-width: 1199px) {
}

@media (max-width: 1199px) {
  .container-logo {
    margin-bottom: 1.5em;
  }

  .container-text-do-login {
    margin-bottom: 1.5em;
  }

  .container-button {
    margin-top: 2em;
  }

  .container-button button {
    width: 100%;
  }

  .container-help-link {
    text-align: center;
  }
}

@media (min-width: 1200px) {
  #view-login {
    width: 350px;
  }

  .container-logo {
    margin-bottom: 2em;
  }

  .container-text-do-login {
    margin-bottom: 3em;
  }
}
.email-suggestion {
  display: block;
  margin-top: 0.35em;
  padding: 0.2em 0;
  border: none;
  background: none;
  color: #1a8ea8;
  font: inherit;
  font-size: 0.88em;
  cursor: pointer;
  text-align: left;
}
.login-link-box {
  text-align: center;
  margin-top: 0.9em;
  font-size: 0.92em;
  color: #555;
}
.login-link-box .link-button {
  min-height: 40px;
}
.login-link-box.highlight {
  background: #eef8fa;
  border-radius: 8px;
  padding: 0.5em 0.6em;
}
</style>
